import { useCallback, useEffect, useState } from 'react';
import {
	makeStyles,
	Text,
	tokens,
	Tab,
	TabList,
	Button
} from '@fluentui/react-components';
import { CheckmarkCircle16Regular } from '@fluentui/react-icons';
import { Page } from 'src/components/ui/page';
import Rules from './rules';
import AutomationRuleAllActivity from './activity';
import { checkProjectAuthorizationService } from 'src/services/appgami-api/check-project-authorization';
import { useAzureProvider } from 'src/azure/context';
import AuthorizationStepsWithModal, { useAuthorizationStepsWithModalRef } from 'src/components/shared/authorization-steps/with-modal';
import { dispatchFinishSetupEventService } from 'src/services/appgami-api/dispatch-finish-setup-event';

export default function HomePage() {
	const classes = useStyles();

	const {applicationContext} = useAzureProvider();
	const finishSetupDialogRef = useAuthorizationStepsWithModalRef();

	const [showFinishSetupButton, setShowFinishSetupButton] = useState(false);
	const [tab, setTab] = useState('rules');

	const onChangeTab = useCallback((value: string) => () => {
		setTab(value);
	}, []);

	const checkAuthorization = useCallback(async () => {
		const result = await checkProjectAuthorizationService({
			organizationName: applicationContext.organizationName,
		});

		setShowFinishSetupButton(result.type === 'success' && !result.data.servicePrincipalAuthorization && !result.data.userAuthorization);
	}, [applicationContext]);

	const finishSetupEvent = useCallback(async () => {
		await dispatchFinishSetupEventService({
			organizationName: applicationContext.organizationName,
			projectId: applicationContext.projectId,
		});
	}, [applicationContext]);

	useEffect(() => {
		checkAuthorization();
	}, []);

	return (
		<Page>
			<div className={classes.content}>
				<div className={classes.header}>
					<Text
						as='h1'
						className={classes.title}
					>
						Appgami automation
					</Text>
					{showFinishSetupButton && (
						<>
							<Button
								style={{ gap: '8px' }}
								appearance="primary"
								onClick={() => {
									finishSetupEvent();
									finishSetupDialogRef.current?.open();
								}}
							>
								<CheckmarkCircle16Regular /> Finish setup
							</Button>
							<AuthorizationStepsWithModal
								ref={finishSetupDialogRef}
								onClose={() => {
									checkAuthorization();
								}}
							/>
						</>
					)}
				</div>
				<TabList selectedValue={tab}>
					<Tab
						onClick={onChangeTab('rules')}
						value="rules"
					>
						Rules
					</Tab>
					<Tab
						onClick={onChangeTab('activity')}
						value="activity"
					>
						Activity
					</Tab>
				</TabList>
				<div className={classes.tabContent}>
					{tab === 'rules' && <Rules />}
					{tab === 'activity' && <AutomationRuleAllActivity />}
				</div>
			</div>
		</Page>
	);
}

const useStyles = makeStyles({
	content: {
		padding: '16px',
		boxSizing: 'border-box'
	},
	header: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingRight: '12px',
	},
	title: {
		padding: '0px 12px',
		fontFamily: tokens.fontFamilyBase,
		fontSize: tokens.fontSizeBase600,
		fontWeight: tokens.fontWeightSemibold,
		lineHeight: tokens.lineHeightBase600,
	},
	tabContent: {
		width: '100%',
		height: '24px',
		padding: '0px 12px',
		boxSizing: 'border-box'
	}
});
